/**
 * Phase 2.5: Dictation Session Page
 * 
 * Full-screen dictation experience accessible from the case timeline.
 * Surgeon dictates once, signs once, and is never pulled back.
 */

import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { DictationScreen } from '@/components/dictation/DictationScreen';
import { toast } from 'sonner';

/** Demo CPTs for when no real authorization is available */
const DEMO_AUTHORIZED_CPTS = [
  { code: '27447', description: 'Total knee arthroplasty' },
  { code: '29881', description: 'Arthroscopic partial meniscectomy' },
  { code: '29877', description: 'Arthroscopic chondroplasty' },
];

export default function DictationSessionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDemoMode } = useDemoMode();

  const authorizationId = searchParams.get('authId');
  const caseNumber = searchParams.get('case') || 'DEMO-001';
  const surgeonId = searchParams.get('surgeonId') || 'demo-surgeon';

  // Parse CPTs from URL or use demo defaults
  const authorizedCpts = useMemo(() => {
    const cptsParam = searchParams.get('cpts');
    if (cptsParam) {
      try {
        return JSON.parse(decodeURIComponent(cptsParam));
      } catch {
        return DEMO_AUTHORIZED_CPTS;
      }
    }
    return DEMO_AUTHORIZED_CPTS;
  }, [searchParams]);

  const handleComplete = (data: {
    transcript: string;
    confirmations: Array<{ cptCode: string; status: string }>;
    signedAt: string;
  }) => {
    toast.success('Dictation Complete', {
      description: 'Operative note signed. Proceeding to reconciliation.',
    });

    // Navigate back to surgeon console or ops queue
    setTimeout(() => {
      navigate('/surgeon');
    }, 1500);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <DictationScreen
      authorizationId={authorizationId}
      caseNumber={caseNumber}
      surgeonId={surgeonId}
      authorizedCpts={authorizedCpts}
      onComplete={handleComplete}
      onCancel={handleCancel}
    />
  );
}
